<script lang="ts" setup>
import { dir } from '@@/bits/current_dir'
import { getDprInteger } from '@@/bits/device'
import type { GeoMap, LatLng, LatLngLiteral } from '@@/bits/map'
import { MapEventsManager, Marker, OverlappingMarkerSpiderfier, Point, Size } from '@@/bits/map'
import type { MapManager } from '@@/bits/map_format/MapManager'
import CustomPopup from '@@/bits/map_popup'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostActionStore } from '@@/pinia/surface_post_action'
import { useSurfacePostConnectionStore } from '@@/pinia/surface_post_connection'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Cid, Id, PostAttributes } from '@@/types'
import SurfacePopupWithTip from '@@/vuecomponents/SurfacePopupWithTip.vue'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import { processedUrl } from '@padlet/vivaldi-client'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useSlots, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    fillColor: string
    strokeColor: string
    position: LatLng | LatLngLiteral
    map: GeoMap | null
    mapManager: MapManager | null
    zIndex?: number
    markerShape?: google.maps.MarkerShape
    allowingGrouping?: boolean
    highlighted?: boolean
    post?: PostAttributes | null
    label?: string
    opened?: boolean
    postReactionIds?: Id[]
    postCommentIds?: Id[]
  }>(),
  {
    zIndex: undefined,
    markerShape: undefined,
    allowingGrouping: true,
    highlighted: false,
    post: null,
    label: undefined,
    opened: undefined,
    postReactionIds: () => [],
    postCommentIds: () => [],
    mapManager: null,
  },
)

const emit = defineEmits<{
  (name: 'reset-viewport'): void
  (name: 'open-marker-popup', cid: Cid): void
  (name: 'close-marker-popup', cid: Cid): void
}>()

// Stores
const surfaceAttachmentsStore = useSurfaceAttachmentsStore()
const surfaceMapStore = useSurfaceMapStore()
const surfaceStore = useSurfaceStore()
const surfacePostsStore = useSurfacePostsStore()
const surfacePermissionsStore = useSurfacePermissionsStore()
const surfacePostActionStore = useSurfacePostActionStore()
const surfacePostConnectionStore = useSurfacePostConnectionStore()

const { canIModerate } = storeToRefs(surfacePermissionsStore)
const { isPostSizeStandard, isPostSizeWide } = storeToRefs(surfaceStore)

const isBeingActedUpon = computed(() => {
  return (
    surfacePostActionStore.postBeingTransferredCid === props.post?.cid ||
    surfacePostActionStore.postBeingCopiedCid === props.post?.cid ||
    surfacePostConnectionStore.postToConnectFromId === props.post?.id ||
    surfaceMapStore.postWithLocationBeingEditedCid === props.post?.cid
  )
})

// Constants
const {
  top: MIN_OFFSET_TOP,
  bottom: MIN_OFFSET_BOTTOM,
  left: MIN_OFFSET_LEFT,
  right: MIN_OFFSET_RIGHT,
} = surfaceMapStore.offsetForMapPopup

// States
const popupContent = ref<HTMLDivElement>()

let marker: Marker
let popup: CustomPopup

const openedOnce = ref(false)
const scrolledAfterOpened = ref(false)
const computedFillColor = ref('FFF')
const computedStrokeColor = ref('000')
const computedZIndex = ref(200)

let scrolledAfterOpenedListener: google.maps.MapsEventListener | undefined
/* ---------------------- */
/* MAP PANNING            */
/* ---------------------- */
const panMapToFitPostInsideOffset = (boundingRect: DOMRect): void => {
  if (!props.map) return

  // Start of panning logic
  // Get the pixel of the current center
  const center = props.map.getCenter()
  if (!center) return
  const centerPixel = popup.fromLatLngToPixel(center)
  if (props.map && centerPixel) {
    // If the top edge of the post is outside the map
    if (boundingRect.top < MIN_OFFSET_TOP) {
      // Move the map down
      centerPixel.y -= MIN_OFFSET_TOP - boundingRect.top
    }
    // If the left edge of the post is outside the map
    if (boundingRect.left < MIN_OFFSET_LEFT) {
      // Move the map to the right
      centerPixel.x -= MIN_OFFSET_LEFT - boundingRect.left
    }
    // If the right edge of the post is outside the map
    if (boundingRect.right > window.innerWidth - MIN_OFFSET_RIGHT) {
      // Move the map to the left
      centerPixel.x += boundingRect.right - (window.innerWidth - MIN_OFFSET_RIGHT)
    }
    // If the bottom edge of the post is outside the map
    if (boundingRect.bottom > window.innerHeight - MIN_OFFSET_BOTTOM) {
      // Move the map up
      centerPixel.y += boundingRect.bottom - (window.innerHeight - MIN_OFFSET_BOTTOM)
    }
    const newCenter = popup.fromPixelToLatLng(centerPixel)
    if (newCenter && !newCenter.equals(center)) {
      props.map.panTo(newCenter)
    }
  }
}

const panMapToMovePostAwayFromHeader = (boundingRect: DOMRect): void => {
  const header = document.getElementById('surface-header')
  if (!header || !props.map) return
  const { bottom: HEADER_BOTTOM, left: HEADER_LEFT, right: HEADER_RIGHT } = header.getBoundingClientRect()
  // Start of panning logic
  // Get the pixel of the current center
  const center = props.map.getCenter()
  if (!center) return
  const centerPixel = popup.fromLatLngToPixel(center)
  if (props.map && centerPixel) {
    const padding = 24 // additional padding so the popup won't touch the edges of the header
    // If the post is intersecting the header
    if (dir() === 'ltr') {
      if (boundingRect.top < HEADER_BOTTOM && boundingRect.left < HEADER_RIGHT) {
        // Always move the map to the right to avoid the header
        centerPixel.x -= HEADER_RIGHT - boundingRect.left + padding
      }
    } else {
      if (boundingRect.top < HEADER_BOTTOM && boundingRect.right > HEADER_LEFT) {
        // Always move the map to the left to avoid the header
        centerPixel.x += boundingRect.right - HEADER_LEFT + padding
      }
    }
    const newCenter = popup.fromPixelToLatLng(centerPixel)
    if (newCenter && !newCenter.equals(center)) {
      props.map.panTo(newCenter)
    }
  }
}

const panMapToFitPost = (postEl?: HTMLElement, tries = 0): void => {
  const el: HTMLElement | undefined = postEl ?? popupContent.value
  if (!el) return
  const boundingRect = el.getBoundingClientRect()
  if (boundingRect.x === 0 && boundingRect.y === 0) {
    // If user clicks on a post in the post list but the post's pin is
    // currently not in viewport, the bounding rect will be (0, 0).
    // The code keeps trying to check the bounding rect but it will
    // be an infinite loop. So break this loop, we can reset the viewport
    // and try again.

    // Give up after 10 tries, can't recurse forever!
    if (tries > 10) return

    // Only reset viewport on the 2nd try onwards because the first time
    // the bounding rect can still be (0, 0) even when the post pin is in
    // viewport. Resetting then will zoom out the map unnecessarily.
    if (tries > 1) {
      emit('reset-viewport')
    }
    nextTick(() => {
      requestAnimationFrame(() => {
        panMapToFitPost(el, tries + 1)
      })
    })
    return
  }
  panMapToFitPostInsideOffset(boundingRect)
  if (surfaceStore.isDesktopLayout && !surfaceStore.isEmbedded) panMapToMovePostAwayFromHeader(boundingRect)
}

const centerMapToPostBeingEdited = () => {
  nextTick(() => {
    if (!props.position || !popupContent.value) return

    props.mapManager?.centerMapToPostBeingEdited(popupContent.value?.getBoundingClientRect().height || 0)
  })
}

const isCurrentPostBeingEdited = computed(() => {
  return (props.post && props.post.cid === surfacePostsStore.postBeingEditedCid) || false
})

watch(isCurrentPostBeingEdited, (isEditing) => {
  if (isEditing) {
    centerMapToPostBeingEdited()
  }
})

/* ---------------------- */
/* MARKER DISPLAY         */
/* ---------------------- */
const MARKER_ICON = {
  anchor: new Point(18, 36), // y = 36 is the bottom of the tip (there is a 12px padding below for the elliptic shadow)
  scaledSize: new Size(36, 48),
  labelOrigin: new Point(18, 14),
}

const setMarkerIcon = () => {
  marker.setIcon({
    ...MARKER_ICON,
    url: processedUrl(
      `https://padlet.com/static/v1/map-marker.svg?fill=${computedFillColor.value}&stroke=${
        computedStrokeColor.value
      }&dot=${marker.getLabel() ? 0 : 1}`,
      {
        width: 36,
        height: 48,
        dpr: getDprInteger(),
      },
    ),
  })
}

const changeMarkerLabel = (label: string | null | undefined, labelColor: string) => {
  if (label) {
    marker.setLabel({
      text: label,
      color: '#' + labelColor,
    })
  } else {
    marker.setLabel(null)
  }

  setMarkerIcon()
}

const showMarker = () => {
  if (props.allowingGrouping) {
    surfaceMapStore.overlappingMarkerSpiderfier?.trackMarker(marker, null as unknown as Function)
  }
  marker.setMap(props.map)
}

const createMarker = () => {
  marker = new Marker({
    position: props.position,
    map: undefined,
    animation: !locationName.value ? undefined : google.maps.Animation.BOUNCE,
    shape: props.markerShape,
    title: props.post ? props.post.subject || locationName.value : '', // a11y
    zIndex: props.zIndex,
    postId: props.post?.id,
  })
  setTimeout(() => {
    marker.setAnimation(null)
  }, 500)
  changeMarkerLabel(props.label, computedStrokeColor.value)
  showMarker()
}

watch(
  () => props.zIndex,
  (newZIndex) => {
    computedZIndex.value = newZIndex as number
  },
)

watch(computedZIndex, (newZIndex) => {
  marker?.setZIndex(newZIndex)
})
/* ---------------------- */
/* POST ATTACHMENT IMAGE  */
/* ---------------------- */
const postAttachmentImage = computed(() => {
  const attachment = props.post ? props.post.attachment : null
  const attachmentLink = props.post ? props.post.attachment_link : null
  if (!attachment) return null
  const displayAttributes = surfaceAttachmentsStore.getLinkDisplayAttributesForUrl(attachment, attachmentLink)
  return displayAttributes ? displayAttributes.image_url : null || null
})

const isPostAttachmentImageLoaded = computed(() => {
  return props.post?.attachment
    ? surfaceAttachmentsStore.isSurfacePreviewImageLoadedForUrl(props.post.attachment)
    : false
})

watch([postAttachmentImage, isPostAttachmentImageLoaded], ([updatedImage, isImageLoaded]) => {
  if (!updatedImage || !isImageLoaded || !props.opened) return
  if (!props.opened) return
  if (!scrolledAfterOpened.value) {
    nextTick(() => {
      // Use next tick so that the img tag finished render before we pan
      panMapToFitPost()
    })
  }
  scrolledAfterOpenedListener?.remove()
  scrolledAfterOpenedListener = undefined
})

/* ---------------------- */
/* LOCATION               */
/* ---------------------- */
const locationName = computed(() => {
  return props.post ? props.post.location_name : undefined
})

/* ---------------------- */
/* POPUP                  */
/* ---------------------- */
const createPopup = () => {
  if (props.post || useSlots().default) {
    popup = new CustomPopup({
      position: props.position,
      map: null,
    })
  }
}

const checkAndOpenPopup = (opened: boolean) => {
  if (opened && !openedOnce.value) {
    openedOnce.value = true
    nextTick(() => {
      popup.setContent(popupContent.value)
    })
  }
  if (opened && !popup.isOpened && props.map) {
    surfaceMapStore.disableMapFittingMarkersBounds()
    popup.open(props.map)

    if (isCurrentPostBeingEdited.value) {
      centerMapToPostBeingEdited()
    }
    nextTick(() => {
      popup.setContent(popupContent.value)
      if (!isCurrentPostBeingEdited.value) {
        panMapToFitPost()
      }
    })
  } else if (!opened && popup.isOpened) {
    popup.close()
  }
}

/* ---------------------- */
/* PROP WATCHERS          */
/* ---------------------- */
watch(
  () => props.map,
  (map) => {
    showMarker()
    if (popup.isOpened) {
      popup.setMap(map)
    } else {
      popup.setMap(null)
    }
  },
)

watch(
  () => props.strokeColor,
  (strokeColor) => {
    computedStrokeColor.value = strokeColor
  },
  {
    immediate: true,
  },
)

watch(
  () => props.fillColor,
  (fillColor) => {
    computedFillColor.value = fillColor
  },
  {
    immediate: true,
  },
)

watch([computedStrokeColor, computedFillColor], () => {
  setMarkerIcon()
  const currentLabel = marker.getLabel()
  if (currentLabel) {
    const labelText = typeof currentLabel === 'string' ? currentLabel : currentLabel.text
    changeMarkerLabel(labelText, computedStrokeColor.value)
  }
})

watch(
  () => props.highlighted,
  (highlighted) => {
    if (highlighted) {
      computedFillColor.value = props.strokeColor
      computedStrokeColor.value = props.fillColor
      computedZIndex.value = 9999
    } else {
      computedFillColor.value = props.fillColor
      computedStrokeColor.value = props.strokeColor
      computedZIndex.value = props.zIndex as number
    }
  },
)

watch(
  () => props.label,
  (label) => {
    changeMarkerLabel(label, computedStrokeColor.value)
  },
)

watch(
  () => props.markerShape,
  (markerShape) => {
    marker.setShape(markerShape || null)
  },
)

watch(
  () => props.position,
  (position) => {
    marker.setPosition(position)
    popup.setPosition(position)
    const isMarkerShowingPinSuggestionsMenu = props.post == null
    if (isMarkerShowingPinSuggestionsMenu) {
      requestAnimationFrame(() => panMapToFitPost())
    }
  },
)

watch(
  () => props.opened,
  (opened) => {
    if (!props.map) return

    checkAndOpenPopup(opened ?? false)
    if (props.post && props.post.attachment) {
      scrolledAfterOpened.value = false
      scrolledAfterOpenedListener = MapEventsManager.addDomListenerOnce(props.map, 'drag', () => {
        scrolledAfterOpened.value = true
      })
    }
  },
)
/* ---------------------- */
/* LIFECYCLE HOOKS        */
/* ---------------------- */

onMounted(() => {
  createMarker()
  marker.addListener('spider_click', () => {
    if (!props.post) return
    // Trigger event to open popup from parent component
    if (!props.opened) {
      emit('open-marker-popup', props.post.cid as Cid)
    } else {
      emit('close-marker-popup', props.post.cid as Cid)
    }
  })
  marker.addListener('spider_format', (status) => {
    if (!surfaceMapStore.overlappingMarkerSpiderfier) return
    if (
      status === OverlappingMarkerSpiderfier.markerStatus.SPIDERFIED ||
      status === OverlappingMarkerSpiderfier.markerStatus.UNSPIDERFIABLE
    ) {
      changeMarkerLabel(null, computedStrokeColor.value)
    }
    if (status === OverlappingMarkerSpiderfier.markerStatus.SPIDERFIABLE) {
      try {
        const nearbyMarkers = surfaceMapStore.overlappingMarkerSpiderfier.markersNearMarker(marker)
        // for some reason nearbyMarkers sometimes includes the current marker
        // => handle this to show correct count for now
        const nearbyMarkersContainsCurrentMarker = nearbyMarkers.some(
          (nearbyMarker) => !!marker.postId && (nearbyMarker as Marker).postId === marker.postId,
        )

        const count = nearbyMarkersContainsCurrentMarker ? nearbyMarkers.length : nearbyMarkers.length + 1 // include current marker
        changeMarkerLabel(String(count), computedStrokeColor.value)
      } catch {
        // do nothing
        /**
         * #WONTFIX
         * ignoring "Must wait for 'idle' event on map before calling markersNearMarker" that unknowningly happen very rarely.
         * Silencing this Sentry error: https://sentry.io/organizations/padlet-y9/issues/1202028855/?project=22080
         * This error makes marker's label not showing the number of markers when loaded, but can still be clicked and behave
         * normally. However if user just zoom in/out of the map the number would appear again.
         */
      }
    }
  })
  marker.addListener('mouseover', () => {
    computedFillColor.value = props.strokeColor
    computedStrokeColor.value = props.fillColor
    computedZIndex.value = 999
  })
  marker.addListener('mouseout', () => {
    computedFillColor.value = props.fillColor
    computedStrokeColor.value = props.strokeColor
    computedZIndex.value = props.zIndex as number
  })
  createPopup()
  checkAndOpenPopup(props.opened ?? false)
})

onBeforeUnmount(() => {
  if (marker) {
    marker.setMap(null)
    surfaceMapStore.overlappingMarkerSpiderfier?.forgetMarker(marker)
  }

  popup.close()
})
</script>

<template>
  <div id="surface-map-google-marker-popup-content" ref="popupContent" tabindex="-1">
    <SurfacePopupWithTip
      v-if="openedOnce"
      :color="!!post ? post.color : 'primary_blur'"
      :should-match-approval-color="post != null && !post.published && canIModerate"
      :is-pulsating="isBeingActedUpon"
    >
      <div class="popup-content">
        <slot>
          <SurfacePost
            v-if="post"
            ref="post"
            :key="post.cid"
            :class="[isPostSizeStandard && 'w-64', isPostSizeWide && 'w-95']"
            :post="post"
            :post-reaction-ids="postReactionIds"
            :post-comment-ids="postCommentIds"
            :put-attachment-on-top="true"
            :x-shadow="false"
            :index="0"
          />
        </slot>
      </div>
    </SurfacePopupWithTip>
  </div>
</template>

<style lang="scss">
@import '@assets/stylesheets/3/modules/all';

.real-wish {
  border-radius: 12px !important;

  overflow: hidden;

  .surface-post-reaction {
    .dropdown-wrapper {
      position: absolute;
    }
  }
  .words {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 12px 0px 0px !important;
    [data-view-mode='show'] & {
      > div:only-child {
        display: flex;
        flex-direction: column;
        overflow: auto;
      }
    }
    [data-view-mode='edit'] & {
      > div:only-child {
        overflow: auto;
      }
    }
    .subject {
      padding: 0px 16px;
      flex-shrink: 0;
    }
    .body {
      padding: 0px 16px;
    }
    .with-body {
      position: relative;
      .body {
        padding-bottom: 12px;
      }
    }
    .with-subject:not(.with-body) {
      .subject {
        padding-bottom: 12px;
      }
    }
    .rich-text-editor.body {
      padding-bottom: 12px;
    }
  }
}
.with-attachment:not(.with-author) {
  .surface-beethoven-preview {
    img {
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
    }
  }
  .attachment-controls.remove-attachment {
    left: 0;
    right: unset !important;
  }
}
/* The popup bubble styling. */
.popup-bubble {
  /* Position the bubble centred-above its parent. */
  position: absolute;
  bottom: 0;
  left: 0;
  transform: translate(-50%);
  /* Style the bubble. */
  border-radius: 5px;
  font-family: sans-serif;
}
/* The parent of the bubble. A zero-height div at the top of the tip. */
.popup-bubble-anchor {
  /* Position the div a fixed distance above the tip. */
  position: absolute;
  flex-shrink: 0;
  width: 100%;
  bottom: /* TIP_HEIGHT= */ 8px;
  left: 0;
}
/* JavaScript will position this div at the bottom of the popup tip. */
.popup-container {
  cursor: auto;
  height: 0;
  position: absolute;
  /* The max width of the info window. */
  width: 200px;
  /* Move the popup to top of the marker */
  transform: translateY(-54px);
}
</style>
